import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { CartItem } from '../components/checkout/CartItem';
import { SEO } from '../components/shared/SEO';
import { MobileCheckoutSummary } from '../components/checkout/MobileCheckoutSummary';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { CouponModal } from '../components/modals/CouponModal';
import { RewardsVouchersSection } from '../components/checkout/RewardsVouchersSection';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { trackBeginCheckout } from '../lib/analytics/events';
import { ShoppingCartIcon, CalendarIcon, TruckIcon, ShoppingBagIcon, ArrowRightIcon, ShieldCheckIcon, AwardIcon, RefreshCwIcon, HeartIcon } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Cart state
  const [cartItems, setCartItems] = useState<any[]>([{
    id: 1,
    name: 'Dyson Corrale Hair Straightener - Prussian Blue/Rich Copper',
    brand: 'Dyson',
    price: 1999,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop',
    type: 'single',
    isGlobal: true
  }, {
    id: 2,
    name: 'Beamarry Argan Smooth Conditioner 380 ML',
    brand: 'Beamarry',
    price: 54.12,
    originalPrice: 85.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop',
    type: 'discount'
  }, {
    id: 3,
    name: 'Panadol Advance 500mg - 24 Tablets',
    brand: 'Panadol',
    price: 12.5,
    quantity: 2,
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop',
    type: 'prescription',
    requiresPrescription: true
  }, {
    id: 4,
    name: 'Skincare Essentials Bundle',
    brand: 'Innova Care',
    price: 245.0,
    originalPrice: 320.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop',
    type: 'bundle',
    bundleItems: [{
      id: 401,
      name: 'Gentle Cleanser 200ml',
      image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=100&h=100&fit=crop'
    }, {
      id: 402,
      name: 'Hydrating Serum 30ml',
      image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=100&h=100&fit=crop'
    }, {
      id: 403,
      name: 'Moisturizing Cream 50ml',
      image: 'https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?w=100&h=100&fit=crop'
    }]
  }, {
    id: 5,
    name: 'La Roche-Posay Anthelios Sunscreen SPF 50+',
    brand: 'La Roche-Posay',
    price: 145.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?w=400&h=400&fit=crop',
    type: 'gift',
    giftItem: {
      id: 501,
      name: 'Travel Size Thermal Spring Water 50ml',
      image: 'https://images.unsplash.com/photo-1556229010-6c3f2c9ca5f8?w=100&h=100&fit=crop'
    }
  }, {
    id: 6,
    name: 'Muscle Recovery Combo Set',
    brand: 'Optimum Nutrition',
    price: 450.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1593095191071-8216adad09a7?w=400&h=400&fit=crop',
    type: 'combo',
    comboItems: [{
      id: 601,
      name: 'Whey Protein 2kg',
      image: 'https://images.unsplash.com/photo-1579722820308-d74e571900a9?w=100&h=100&fit=crop'
    }, {
      id: 602,
      name: 'BCAA Powder 300g',
      image: 'https://images.unsplash.com/photo-1593095191071-8216adad09a7?w=100&h=100&fit=crop'
    }, {
      id: 603,
      name: 'Shaker Bottle 700ml',
      image: 'https://images.unsplash.com/photo-1594498653385-d5172c532c00?w=100&h=100&fit=crop'
    }]
  }, {
    id: 7,
    name: 'Sensodyne Rapid Relief Toothpaste',
    brand: 'Sensodyne',
    price: 32.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1559591937-e68fb3305e5d?w=400&h=400&fit=crop',
    type: 'double'
  }]);
  // Saved for later state
  const [savedForLater, setSavedForLater] = useState([{
    id: 301,
    name: 'Hair Serum Treatment Oil',
    price: 89.99,
    image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&h=400&fit=crop'
  }, {
    id: 302,
    name: 'Heat Protection Spray',
    price: 45.99,
    image: 'https://images.unsplash.com/photo-1629198735700-4f4791ab66b7?w=400&h=400&fit=crop'
  }]);
  // Other state
  const [orderNotes, setOrderNotes] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = 451.86;
  const tax = subtotal * 0.15;
  const total = subtotal - discount + tax;
  // Installment options
  const installmentOptions = [{
    provider: 'Tamara',
    installments: 12,
    amount: (total / 12).toFixed(2),
    color: 'bg-[#1a1a1a]'
  }, {
    provider: 'Tabby',
    installments: 4,
    amount: (total / 4).toFixed(2),
    color: 'bg-[#3EDDB4]'
  }, {
    provider: 'MADA',
    installments: 4,
    amount: (total / 4).toFixed(2),
    color: 'bg-[#00A8E1]'
  }];
  // Handler functions
  const moveToCart = (itemId: number) => {
    const item = savedForLater.find(i => i.id === itemId);
    if (item) {
      setCartItems([...cartItems, {
        ...item,
        quantity: 1
      }]);
      setSavedForLater(savedForLater.filter(i => i.id !== itemId));
    }
  };
  const removeSavedItem = (itemId: number) => {
    setSavedForLater(savedForLater.filter(i => i.id !== itemId));
  };
  const getEstimatedDeliveryDate = () => {
    const today = new Date();
    const deliveryDate = new Date(today.setDate(today.getDate() + 2));
    return deliveryDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };
  const applyCoupon = (code: string) => {
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) return;
    setCouponCode(normalizedCode);
  };
  // Recently viewed products
  const recentlyViewed = [{
    id: 201,
    name: 'Professional Hair Dryer 2000W',
    brand: 'SalonTech',
    price: 299.99,
    originalPrice: 399.99,
    discount: 25,
    rating: 4.9,
    reviewCount: 342,
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=400&fit=crop'
  }, {
    id: 202,
    name: 'Keratin Hair Mask 500ml',
    brand: 'HairCare Pro',
    price: 79.99,
    originalPrice: 99.99,
    discount: 20,
    rating: 4.7,
    reviewCount: 189,
    image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&h=400&fit=crop'
  }, {
    id: 203,
    name: 'Curl Defining Cream',
    brand: 'CurlMaster',
    price: 56.99,
    originalPrice: 56.99,
    discount: 0,
    rating: 4.8,
    reviewCount: 234,
    image: 'https://images.unsplash.com/photo-1629198735700-4f4791ab66b7?w=400&h=400&fit=crop'
  }, {
    id: 204,
    name: 'Hair Straightening Serum',
    brand: 'SmoothSilk',
    price: 42.99,
    originalPrice: 59.99,
    discount: 28,
    rating: 4.6,
    reviewCount: 167,
    image: 'https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=400&h=400&fit=crop'
  }, {
    id: 205,
    name: 'Volume Boosting Mousse',
    brand: 'VolumePro',
    price: 47.99,
    originalPrice: 64.99,
    discount: 26,
    rating: 4.5,
    reviewCount: 198,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop'
  }, {
    id: 206,
    name: 'Anti-Frizz Hair Oil',
    brand: 'FrizzControl',
    price: 52.99,
    originalPrice: 72.99,
    discount: 27,
    rating: 4.8,
    reviewCount: 223,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop'
  }];
  const handleProceedToCheckout = () => {
    // Track begin_checkout event
    trackBeginCheckout(cartItems.map(item => ({
      item_id: item.id.toString(),
      item_name: item.name,
      price: item.price,
      quantity: item.quantity
    })));
    navigate('/checkout');
  };
  return <HelmetProvider data-id="element-6596" data-mp="2fg37za1hsumt">
      <SEO title="Cart" description="Review your cart at Innova Pharmacy — check quantities, prices, and promotions before checkout. Online pharmacy with fast delivery across Saudi Arabia." canonical="/en/cart/" robots="noindex,noimageindex,noarchive,follow" hreflangAr="/cart/" hreflangEn="/en/cart/" hreflangDefault="/cart/" data-id="element-6597" data-mp="2feidfkf6hboy" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6598" data-mp="v0i1evgphmxt">
        {/* Mobile Header - Only on mobile */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6599" data-mp="g8crnpqncmn8">
          <MobileInternalHeader title="Shopping Cart" data-id="element-6600" data-mp="b7ynwjw2vyv0" />
        </div>
        {/* Desktop Header - Only on desktop */}
        <div className="hidden md:block" data-id="element-6601" data-mp="6age6ra8lfly">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6602" data-mp="6b8ci9v7xrfx" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)] pb-40 md:pb-0" data-id="element-6603" data-mp="u00z3fpyamxv">
          <h1 className="sr-only" data-id="element-6604" data-mp="p7hpsuyaqdyv">Shopping Cart</h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Shopping Cart'
        }]} title="Shopping Cart" description="Review your items and proceed to checkout" data-id="element-6605" data-mp="k9y8tna7rn38" />
          <div className="container mx-auto px-4 py-4 md:py-6 lg:py-8" data-id="element-6606" data-mp="1l8kpcq9xvcmm">
            {cartItems.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-6607" data-mp="1p9wfcwwq5zvc">
                {/* Cart Items - Main Area */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6" data-id="element-6608" data-mp="1z6si0lzxzrrz">
                  {/* Free Shipping Progress Bar */}
                  <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-blue-100" data-id="element-6609" data-mp="244kjo7qe2s9d">
                    <div className="flex items-center justify-between mb-2" data-id="element-6610" data-mp="285mkgszyfcdg">
                      <div className="flex items-center gap-2" data-id="element-6611" data-mp="2cxe1abtb63qe">
                        <TruckIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-6612" data-mp="2d00aefvukraq" />
                        <span className="font-bold text-gray-900" data-id="element-6613" data-mp="2hvz32fo7kvgy">
                          Free Shipping
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#1E2B55]" data-id="element-6614" data-mp="2ru5650lr47ne">
                        {total >= 200 ? 'Unlocked!' : `Add ${(200 - total).toFixed(2)} SAR more`}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-6615" data-mp="2bwc8tonyg8cl">
                      <div className={`h-2.5 rounded-full ${total >= 200 ? 'bg-green-500' : 'bg-[#1E2B55]'}`} style={{
                    width: `${Math.min(total / 200 * 100, 100)}%`
                  }} data-id="element-6616" data-mp="275vt1xwnwan2"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-6617" data-mp="2uvzupzqai0bf">
                      Free shipping on orders over 200 SAR
                    </p>
                  </div>

                  {/* Cart Items - Shipments */}
                  <div className="space-y-6" data-id="element-6618" data-mp="10igps4txjfg3">
                    {/* Shipment 1 */}
                    {cartItems.slice(0, Math.ceil(cartItems.length / 2)).length > 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-6619" data-mp="37560rzopd2ae">
                        <div className="bg-[#4ADE80] px-4 py-3 flex items-center justify-between text-white" data-id="element-6620" data-mp="3c524dxthmrpf">
                          <span className="font-bold text-sm" data-id="element-6621" data-mp="2xeuy0ggr8z53">Shipment 1 of 2</span>
                          <div className="flex items-center gap-2" data-id="element-6622" data-mp="32e7hqscbxq3g">
                            <span className="border border-white/40 rounded px-2 py-0.5 text-[10px] font-bold flex items-center gap-1" data-id="element-6623" data-mp="2of7t3kothb7a">
                              <TruckIcon className="w-3 h-3" data-id="element-6624" data-mp="2sh1obdg8tn02" /> Express
                            </span>
                            <span className="text-xs font-medium" data-id="element-6625" data-mp="2ei218a8dpg60">Same day delivery</span>
                          </div>
                        </div>
                        <div className="p-3 md:p-4 lg:p-6" data-id="element-6626" data-mp="2jhy6ecstbdn5">
                          <div className="space-y-3 md:space-y-4" data-id="element-6627" data-mp="cqyptiqnzxke">
                            {cartItems.slice(0, Math.ceil(cartItems.length / 2)).map(item => <CartItem key={item.id} item={item} onQuantityChange={(id, quantity) => {
                        setCartItems(cartItems.map(i => i.id === id ? {
                          ...i,
                          quantity
                        } : i));
                      }} onRemove={id => {
                        setCartItems(cartItems.filter(i => i.id !== id));
                      }} onSaveForLater={id => {
                        const itemToSave = cartItems.find(i => i.id === id);
                        if (itemToSave) {
                          setSavedForLater([...savedForLater, itemToSave]);
                          setCartItems(cartItems.filter(i => i.id !== id));
                        }
                      }} data-id="element-6628" data-mp="2dsep53kieea0" />)}
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-100 flex gap-3" data-id="element-6629" data-mp="3sog0jqt9b3sr">
                            <button className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-6630" data-mp="kx01qx8cwd1">
                              Delete all
                            </button>
                            <button className="flex-1 py-2.5 border border-[#1E2B55] rounded-lg text-sm font-bold text-[#1E2B55] hover:bg-blue-50 transition-colors" data-id="element-6631" data-mp="3506k74etb890">
                              Move all to wishlist
                            </button>
                          </div>
                        </div>
                      </div>}

                    {/* Shipment 2 */}
                    {cartItems.slice(Math.ceil(cartItems.length / 2)).length > 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-6632" data-mp="3ol9zedavo8uq">
                        <div className="bg-[#1E2B55] px-4 py-3 flex items-center justify-between text-white" data-id="element-6633" data-mp="3jmfdv3lj066x">
                          <span className="font-bold text-sm" data-id="element-6634" data-mp="25xl9zld34fm">Shipment 2 of 2</span>
                          <div className="flex items-center gap-2" data-id="element-6635" data-mp="3tecrsi3x5c9c">
                            <span className="border border-white/40 rounded px-2 py-0.5 text-[10px] font-bold flex items-center gap-1" data-id="element-6636" data-mp="35mxoam0eoowi">
                              <TruckIcon className="w-3 h-3" data-id="element-6637" data-mp="30pxga5jzxc2r" /> Standard
                            </span>
                            <span className="text-xs font-medium" data-id="element-6638" data-mp="3epgoeyct4nf2">2-3 business days</span>
                          </div>
                        </div>
                        <div className="p-3 md:p-4 lg:p-6" data-id="element-6639" data-mp="txdh8k8t63ii">
                          <div className="space-y-3 md:space-y-4" data-id="element-6640" data-mp="tzzosjvp8j0q">
                            {cartItems.slice(Math.ceil(cartItems.length / 2)).map(item => <CartItem key={item.id} item={item} onQuantityChange={(id, quantity) => {
                        setCartItems(cartItems.map(i => i.id === id ? {
                          ...i,
                          quantity
                        } : i));
                      }} onRemove={id => {
                        setCartItems(cartItems.filter(i => i.id !== id));
                      }} onSaveForLater={id => {
                        const itemToSave = cartItems.find(i => i.id === id);
                        if (itemToSave) {
                          setSavedForLater([...savedForLater, itemToSave]);
                          setCartItems(cartItems.filter(i => i.id !== id));
                        }
                      }} data-id="element-6641" data-mp="xesw6mxppufu" />)}
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-100 flex gap-3" data-id="element-6642" data-mp="3r7sgnx7prza3">
                            <button className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-6643" data-mp="3m8fmereijpxh">
                              Delete all
                            </button>
                            <button className="flex-1 py-2.5 border border-[#1E2B55] rounded-lg text-sm font-bold text-[#1E2B55] hover:bg-blue-50 transition-colors" data-id="element-6644" data-mp="irpci1ft8sqi">
                              Move all to wishlist
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>

                  <div className="mt-4 lg:hidden" data-id="element-6645" data-mp="2the2kczvl67m">
                    <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} data-id="element-6646" data-mp="2jfa9qb70gpnq" />
                  </div>
                </div>

                {/* Right Column - Desktop Only */}
                <div className="hidden lg:block lg:col-span-1" data-id="element-6647" data-mp="324qdr8hwn08w">
                  <div className="sticky top-24 space-y-6" data-id="element-6648" data-mp="374cvnv8oh4o1">
                    <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} data-id="element-6649" data-mp="376pjgp14a990" />
                    <OrderSummary subtotal={subtotal} discount={discount} tax={tax} total={total} itemCount={cartItems.reduce((acc, item) => acc + item.quantity, 0)} onOpenCoupons={() => setCouponModalOpen(true)} data-id="element-6650" data-mp="1mohybqv0vlsa" />
                  </div>
                </div>
              </div> : <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-8" data-id="element-6651" data-mp="dcvcym14jeuo">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6652" data-mp="ic8ca696mee5">
                  <ShoppingBagIcon className="w-10 h-10 text-gray-400" data-id="element-6653" data-mp="mfo32lyklkix" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" data-id="element-6654" data-mp="rcmzxc9mc5kt">
                  Your cart is empty
                </h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-6655" data-mp="2o82mxqkocz1z">
                  Looks like you haven't added anything to your cart yet.
                  Explore our top categories and find what you need.
                </p>
                <button onClick={() => navigate('/')} className="inline-flex items-center justify-center gap-2 bg-[#1E2B55] text-white font-bold py-3 px-8 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-6656" data-mp="2gv3bwod4do66">
                  Continue Shopping
                  <ArrowRightIcon className="w-5 h-5" data-id="element-6657" data-mp="34k5t5v3udf05" />
                </button>
              </div>}

            {/* Recently Viewed (Empty State) */}
            {cartItems.length === 0 && <div className="mt-6 md:mt-8" data-id="element-6658" data-mp="2uloyan1s8g8t">
                <div className="flex items-center justify-between mb-4 md:mb-6" data-id="element-6659" data-mp="2uknadhzo3z9o">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold" data-id="element-6660" data-mp="2qjuzylf27obt">
                    Recently Viewed
                  </h2>
                </div>
                <div className="flex overflow-x-auto md:grid md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 pb-4 md:pb-0 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-id="element-6661" data-mp="1f5hh5uz5xz2n">
                  {recentlyViewed.map(product => <div key={product.id} className="flex-none w-[160px] md:w-auto bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow snap-start" data-id="element-6662" data-mp="1lkokqaf44n46">
                      <div className="relative aspect-square bg-gray-50 overflow-hidden" data-id="element-6663" data-mp="xwf5jrcemxm0">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-6664" data-mp="12mvkxgzsctb0" />
                        {product.discount > 0 && <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-6665" data-mp="sxc1oa85uxfm">
                            -{product.discount}%
                          </div>}
                      </div>
                      <div className="p-2 md:p-3" data-id="element-6666" data-mp="9su5qurwlb3">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide" data-id="element-6667" data-mp="cotnjm9ri1n">
                          {product.brand}
                        </p>
                        <h3 className="text-xs md:text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[32px] md:min-h-[40px]" data-id="element-6668" data-mp="9c21y8ynq0ks">
                          {product.name}
                        </h3>
                        <div className="flex flex-col mb-2 md:mb-3" data-id="element-6669" data-mp="j85dg5fefbke">
                          {product.originalPrice && <span className="text-xs text-gray-400 line-through" data-id="element-6670" data-mp="e89am9iftq6f">
                              {product.originalPrice} SAR
                            </span>}
                          <span className="text-sm md:text-base font-bold text-red-600" data-id="element-6671" data-mp="o29lks0ehpn2">
                            {product.price} SAR
                          </span>
                        </div>
                        <button className="w-full bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-1.5 md:py-2 px-2 md:px-4 rounded-lg transition-colors text-xs md:text-sm" data-id="element-6672" data-mp="wyqpba6fql8s">
                          Add to Cart
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </main>
        <div className="hidden md:block" data-id="element-6673" data-mp="y4dbom1vrs39">
          <Footer data-id="element-6674" data-mp="y5evbewypmwl" />
        </div>

        {cartItems.length > 0 && <MobileCheckoutSummary subtotal={subtotal} discount={discount} tax={tax} total={total} onCheckout={handleProceedToCheckout} data-id="element-6675" data-mp="124x6hiyzejm8" />}

        <MobileFooterNav data-id="element-6676" data-mp="1c4wd3abubzwg" />
        <div className="hidden md:block" data-id="element-6677" data-mp="1c569bfnhb3al">
          <PharmacistChat data-id="element-6678" data-mp="2ik725ch7j5cy" />
        </div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6679" data-mp="2ijf9a9ezvlqd" />
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        applyCoupon(code);
        setCouponModalOpen(false);
      }} data-id="element-6680" data-mp="6w20na1hmjsv" />
      </div>
    </HelmetProvider>;
}